import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Intelligently formats text content for display.
 * - Detects if content is properly formatted HTML (has h2, h3, ul, ol, strong, etc.)
 * - If content only has basic <p> tags (from TipTap paste), extracts text and applies formatting
 * - Converts plain text patterns to proper HTML structure:
 *   - "SECTION NAME:" or "TEXT ===" or "=== TEXT ===" → <h3> headings
 *   - "- item" or "• item" or "✓ item" or "✗ item" → <ul><li> lists
 *   - "1. item" → <ol><li> numbered lists
 *   - Double line breaks → <p> paragraphs
 *   - Single line breaks → <br>
 */
export function formatDisplayContent(content: string | null | undefined): string {
  if (!content) return '';
  
  // Check if content has rich formatting (not just <p> tags from TipTap paste)
  const hasRichFormatting = /<(h[1-6]|ul|ol|li|strong|em|a|table|blockquote)[^>]*>/i.test(content);
  if (hasRichFormatting) return content; // Already properly formatted
  
  // Extract text from basic <p> tags if present (TipTap paste scenario)
  let textContent = content;
  const hasOnlyParagraphs = /<p[^>]*>[\s\S]*?<\/p>/i.test(content) && !hasRichFormatting;
  
  if (hasOnlyParagraphs) {
    // Extract text content from <p> tags, preserving line breaks
    textContent = content
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  }
  
  // Check if it's already plain text without tags
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(textContent);
  if (hasHtmlTags && !hasOnlyParagraphs) return content; // Has other HTML, return as-is
  
  // Format plain text with rich patterns
  let formatted = textContent
    // Convert section headers with trailing === like "CLINICAL EXAMINATION BRIEFING ==="
    .replace(/^([A-Z][A-Z\s&-]{3,})\s*={2,}\s*$/gm, '<h3 class="font-bold text-lg mt-6 mb-3 text-primary border-b border-primary/20 pb-2">$1</h3>')
    
    // Convert === SECTION === style headers (equals on both sides)
    .replace(/^={2,}\s*(.+?)\s*={2,}$/gm, '<h3 class="font-bold text-lg mt-6 mb-3 text-primary border-b border-primary/20 pb-2">$1</h3>')
    
    // Convert UPPERCASE SECTION: headers (uppercase words followed by colon, alone or at start of line)
    .replace(/^([A-Z][A-Z\s&-]{3,}):\s*/gm, '<h3 class="font-bold text-base mt-5 mb-2 text-foreground">$1:</h3>')
    
    // Convert checkmark/x mark items to styled list items
    .replace(/^[✓✔]\s+(.+)$/gm, '<li class="text-success flex items-start gap-2"><span class="text-success">✓</span> $1</li>')
    .replace(/^[✗✘×]\s+(.+)$/gm, '<li class="text-destructive flex items-start gap-2"><span class="text-destructive">✗</span> $1</li>')
    
    // Convert bullet points (-, •, *, ✓, ✗)
    .replace(/^[-•*]\s+(.+)$/gm, '<li>$1</li>')
    
    // Convert numbered lists  
    .replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
    
    // Wrap consecutive <li> items in <ul> (excluding already styled ones)
    .replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, (match) => {
      // Check if items already have special styling
      if (match.includes('text-success') || match.includes('text-destructive')) {
        return `<ul class="list-none space-y-2 my-3 pl-2">${match}</ul>`;
      }
      return `<ul class="list-disc pl-6 space-y-1 my-3">${match}</ul>`;
    })
    
    // Convert double line breaks to paragraphs
    .replace(/\n\n+/g, '</p><p class="mb-3">')
    
    // Convert single line breaks to <br>
    .replace(/\n/g, '<br>');
  
  // Wrap in paragraph if not starting with a block element
  if (!formatted.startsWith('<h') && !formatted.startsWith('<ul') && !formatted.startsWith('<ol') && !formatted.startsWith('<p')) {
    formatted = '<p class="mb-3">' + formatted + '</p>';
  }
  
  // Clean up empty paragraphs
  formatted = formatted
    .replace(/<p[^>]*>\s*<\/p>/g, '')
    .replace(/<p[^>]*><br><\/p>/g, '')
    .replace(/<br>\s*<h3/g, '<h3')
    .replace(/<\/h3><br>/g, '</h3>');
  
  return formatted;
}
